import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ApiClientError,
  deleteComment,
  deletePost,
  fetchDivinationHistory,
  fetchMyComments,
  fetchMyLikes,
  fetchMyPosts,
  fetchMyStats,
  historyQuestionLabel,
  historySystem,
  likePost,
  parseDaLiuRenResult,
  parseDivinationResult,
  updateMe,
  type DivinationHistoryItem,
  type PostItem,
} from '@suangua/shared';
import { useAuth } from '../lib/auth/AuthContext';
import { themeLabel, useUI } from '../lib/ui/UIContext';
import { Avatar, fortuneDotClass, postTitle } from '../components/ui/atoms';

/**
 * 我的页（原型 me 屏复刻）：
 * 左资料卡（头像/昵称可改 + 统计 + 退出登录）
 * 右功能卡（常用功能 / 最近占卜）+ 三列表（我的帖子/回复/点赞）。
 */

type Tab = 'posts' | 'comments' | 'likes';

export function MePage() {
  const { user, loading, setUser, logout } = useAuth();
  const { showToast, theme, toggleTheme } = useUI();
  const navigate = useNavigate();

  const [tab, setTab] = useState<Tab>('posts');
  const [stats, setStats] = useState<{ posts: number; comments: number; likes_received: number } | null>(null);
  const [posts, setPosts] = useState<PostItem[]>([]);
  const [comments, setComments] = useState<Awaited<ReturnType<typeof fetchMyComments>>['items']>([]);
  const [likes, setLikes] = useState<PostItem[]>([]);
  const [history, setHistory] = useState<DivinationHistoryItem[]>([]);
  const [profileOpen, setProfileOpen] = useState(false);

  /** 拉取全部数据（统计 / 三列表 / 最近占卜） */
  const load = useCallback(async () => {
    if (!user) return;
    try {
      const [s, p, c, l] = await Promise.all([
        fetchMyStats(),
        fetchMyPosts(),
        fetchMyComments(),
        fetchMyLikes(),
      ]);
      setStats(s);
      setPosts(p.items);
      setComments(c.items);
      setLikes(l.items);
    } catch {
      /* 静默：数据加载失败不阻塞页面 */
    }
    try {
      const h = await fetchDivinationHistory();
      setHistory(h.items.slice(0, 3));
    } catch {
      /* 占卜历史可选 */
    }
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="empty-card">
        <i className="fa-regular fa-user" />
        <p>加载中…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="empty-card">
        <i className="fa-regular fa-user" />
        <p>请先登录后查看个人中心</p>
        <p className="text-[11px]">点击右上角「登录 / 注册」</p>
      </div>
    );
  }

  /** 切换列表 Tab 并滚动到列表区 */
  function switchTab(t: Tab) {
    setTab(t);
    document.getElementById('me-lists')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <section>
      <div className="page-head">
        <h1 className="page-title font-zh">我的</h1>
        <span className="page-sub">UID · {user.uid}</span>
      </div>

      <div className="me-grid">
        {/* ══ 左：资料卡 ══ */}
        <div className="ink-card relative self-start overflow-hidden p-6">
          <div className="flex items-center gap-4">
            <button className="relative border-0 bg-none p-0" onClick={() => setProfileOpen(true)} aria-label="更改资料">
              <Avatar name={user.username} url={user.avatar_url} size="lg" ring />
              <span className="cam-badge">
                <i className="fa-solid fa-camera" />
              </span>
            </button>
            <div className="min-w-0 flex-1">
              <p className="font-zh text-xl font-bold text-t1">{user.username}</p>
              <p className="mt-1 text-[12px] text-t2">UID · {user.uid}</p>
              <p className="mt-0.5 truncate text-[11px] text-t3">{user.email}</p>
            </div>
          </div>

          {/* 统计（服务端真实数据） */}
          <div className="mt-5 grid grid-cols-3 divide-x divide-line rounded-xl bg-fill-deep py-4 text-center">
            <div>
              <p className="text-xl font-bold text-gold-400">{stats?.posts ?? 0}</p>
              <p className="mt-0.5 text-[11px] text-t2">帖子</p>
            </div>
            <div>
              <p className="text-xl font-bold text-gold-400">{stats?.comments ?? 0}</p>
              <p className="mt-0.5 text-[11px] text-t2">回复</p>
            </div>
            <div>
              <p className="text-xl font-bold text-gold-400">{stats?.likes_received ?? 0}</p>
              <p className="mt-0.5 text-[11px] text-t2">获赞</p>
            </div>
          </div>

          <button
            className="ghost-btn mt-4"
            onClick={() => {
              void logout().then(() => showToast('已退出登录'));
            }}
          >
            <i className="fa-solid fa-right-from-bracket" /> 退出登录
          </button>
        </div>

        {/* ══ 右：功能卡 + 最近占卜 ══ */}
        <div className="space-y-5">
          <div>
            <h2 className="section-title">常 用 功 能</h2>
            <div className="menu-grid">
              <MenuCard icon="fa-regular fa-file-lines" label="我的帖子" badge={posts.length} onClick={() => switchTab('posts')} />
              <MenuCard icon="fa-regular fa-comment-dots" label="我的回复" badge={comments.length} onClick={() => switchTab('comments')} />
              <MenuCard icon="fa-regular fa-heart" label="我的点赞" badge={likes.length} onClick={() => switchTab('likes')} />
              <MenuCard icon="fa-solid fa-clock-rotate-left" label="占卜历史" onClick={() => navigate('/divination')} />
              <MenuCard
                icon="fa-solid fa-circle-half-stroke"
                label={`外观 · ${themeLabel(theme)}（${theme === 'dark' ? '暗' : '亮'}）`}
                onClick={toggleTheme}
              />
              <MenuCard icon="fa-solid fa-gear" label="设置" onClick={() => showToast('设置暂未开放')} />
            </div>
          </div>

          <div>
            <h2 className="section-title">最 近 占 卜</h2>
            <div className="ink-card divide-y divide-line">
              {history.length === 0 ? (
                <p className="py-6 text-center text-[12px] text-t3">
                  {user ? '暂无占卜记录，去起一卦吧' : '登录后可见'}
                </p>
              ) : (
                history.map((h) => {
                  // 按体系分派解析（旧数据无 system → 六神煞）；大六壬结果没有 final 字段，不能直接读
                  const isDa = historySystem(h) === 'daliuren';
                  const parsed = isDa ? null : parseDivinationResult(h);
                  const da = isDa ? parseDaLiuRenResult(h) : null;
                  const fortune = isDa ? da?.interpretation.fortune : parsed?.final.fortune;
                  const name = isDa ? (da?.keTi.name ?? '大六壬') : (parsed?.final.name ?? '卦');
                  const modeText =
                    h.mode === 'calendar' ? '日历取数' : h.mode === 'stroke' ? '随机取数' : '时间起课';
                  const systemText = isDa ? '大六壬' : '六神煞';
                  return (
                    <div key={h.id} className="flex items-center gap-3 px-5 py-3">
                      <span className={`h-2 w-2 shrink-0 rounded-full ${fortuneDotClass(fortune ?? '')}`} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] text-t1">
                          <span className="font-zh font-semibold text-gold-400">{name}</span>
                          <span className="text-t2"> · {historyQuestionLabel(h)}</span>
                        </p>
                      </div>
                      <span className="shrink-0 text-[10px] text-t3">
                        {fortune ? `${fortune} · ` : ''}
                        {systemText} · {modeText}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ══ 三列表（我的帖子 / 回复 / 点赞） ══ */}
      <div id="me-lists" className="mt-6 scroll-mt-24">
        <div className="seg">
          {(
            [
              ['posts', `我的帖子（${posts.length}）`],
              ['comments', `我的回复（${comments.length}）`],
              ['likes', `我的点赞（${likes.length}）`],
            ] as Array<[Tab, string]>
          ).map(([key, label]) => (
            <button key={key} className={tab === key ? 'on' : ''} onClick={() => setTab(key)}>
              {label}
            </button>
          ))}
        </div>

        <div className="mt-3 space-y-2">
          {tab === 'posts' && <MyPostRows items={posts} onDeleted={load} />}
          {tab === 'comments' &&
            (comments.length === 0 ? (
              <EmptyRow text="还没有回复过" />
            ) : (
              comments.map((c) => (
                <MyCommentRow
                  key={c.id}
                  item={c}
                  onDeleted={async () => {
                    await load();
                    showToast('回复已删除');
                  }}
                />
              ))
            ))}
          {tab === 'likes' &&
            (likes.length === 0 ? (
              <EmptyRow text="还没有点赞" />
            ) : (
              likes.map((p) => (
                <div key={p.id} className="ink-card flex items-center justify-between p-4">
                  <button
                    className="min-w-0 flex-1 truncate text-left text-[13px] text-t1"
                    onClick={() => navigate(`/board/${p.id}`)}
                  >
                    {postTitle(p)}
                  </button>
                  <button
                    className="shrink-0 rounded px-2 py-1 text-[11px] text-t3 hover:text-cinnabar-500"
                    onClick={() =>
                      void likePost(p.id, true).then(() => {
                        void load();
                        showToast('已取消点赞');
                      })
                    }
                  >
                    取消赞
                  </button>
                </div>
              ))
            ))}
        </div>
      </div>

      {/* 更改资料弹层（头像预览 + 昵称 + 头像直链） */}
      {profileOpen && (
        <ProfileEditModal
          user={user}
          onClose={() => setProfileOpen(false)}
          onSaved={(updated) => {
            setUser(updated);
            setProfileOpen(false);
            showToast('资料已更新');
          }}
        />
      )}
    </section>
  );
}

/** 功能卡（menu-card） */
function MenuCard(props: { icon: string; label: string; badge?: number; onClick: () => void }) {
  return (
    <button className="ink-card menu-card" onClick={props.onClick}>
      <i className={`${props.icon} lead`} />
      <span>{props.label}</span>
      {typeof props.badge === 'number' ? (
        <span className="badge">{props.badge}</span>
      ) : (
        <i className="fa-solid fa-angle-right ml-auto text-t3" />
      )}
    </button>
  );
}

/** 我的回复行（点击跳原帖；可删除——楼中楼子回复将级联一并删除） */
function MyCommentRow(props: {
  item: Awaited<ReturnType<typeof fetchMyComments>>['items'][number];
  onDeleted: () => Promise<void>;
}) {
  const { item: c, onDeleted } = props;
  const navigate = useNavigate();
  const { showToast } = useUI();

  return (
    <div className="ink-card p-4">
      <button className="block w-full text-left" onClick={() => navigate(`/board/${c.post_id}`)}>
        <p className="line-clamp-1 text-[13px] text-t1">{c.content}</p>
        <p className="mt-1 text-[11px] text-t3">
          回复于「{c.post_title || (c.post_board === 'divination' ? `占卜贴 #${c.post_id}` : '无标题帖')}」
        </p>
      </button>
      <div className="mt-1 flex justify-end">
        <button
          className="text-[11px] text-t3 transition-colors hover:text-cinnabar-500"
          onClick={() => {
            if (!window.confirm('确认删除该回复？其楼中楼子回复将一并删除。')) return;
            deleteComment(c.id)
              .then(() => void onDeleted())
              .catch((e) => showToast(e instanceof ApiClientError ? e.message : '删除失败'));
          }}
        >
          <i className="fa-regular fa-trash-can mr-0.5" /> 删除
        </button>
      </div>
    </div>
  );
}

/** 我的帖子行（可删除） */
function MyPostRows({ items, onDeleted }: { items: PostItem[]; onDeleted: () => Promise<void> }) {
  const navigate = useNavigate();
  const { showToast } = useUI();
  const { user } = useAuth();

  if (items.length === 0) return <EmptyRow text="还没有发过帖" />;

  return (
    <>
      {items.map((p) => (
        <div key={p.id} className="ink-card p-4">
          <button className="block w-full text-left" onClick={() => navigate(`/board/${p.id}`)}>
            <p className="text-[13px] font-medium text-t1">
              {postTitle(p)}
              {p.is_hidden === 1 && (
                <span className="ml-1.5 align-middle text-[10px] font-normal text-cinnabar-500">[已隐藏]</span>
              )}
            </p>
          </button>
          <div className="mt-1 flex items-center justify-between text-[11px] text-t3">
            <span>
              <i className="fa-regular fa-thumbs-up" /> {p.like_count} · <i className="fa-regular fa-comment" />{' '}
              {p.reply_count}
            </span>
            {user?.id === p.user_id && (
              <button
                className="hover:text-cinnabar-500"
                onClick={(e) => {
                  e.stopPropagation();
                  void deletePost(p.id).then(() => {
                    void onDeleted();
                    showToast('帖子已删除');
                  });
                }}
              >
                删除
              </button>
            )}
          </div>
        </div>
      ))}
    </>
  );
}

/** 空态行 */
function EmptyRow({ text }: { text: string }) {
  return (
    <p className="rounded-xl border border-dashed border-line bg-fill p-6 text-center text-[13px] text-t3">{text}</p>
  );
}

/** 更改资料弹层（原型「更换头像」模态框扩展：含昵称） */
function ProfileEditModal(props: {
  user: NonNullable<ReturnType<typeof useAuth>['user']>;
  onClose: () => void;
  onSaved: (user: ReturnType<typeof useAuth>['user']) => void;
}) {
  const { user, onClose, onSaved } = props;
  const { showToast } = useUI();
  const [username, setUsername] = useState(user.username);
  const [avatar, setAvatar] = useState(user.avatar_url ?? '');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  // 头像直链合法 → 预览
  const avatarPreview = /^https?:\/\/\S+$/i.test(avatar.trim()) ? avatar.trim() : '';

  /** 保存资料（昵称 + 头像直链）；返回是否成功 */
  async function save(avatarUrl: string | null): Promise<boolean> {
    const name = username.trim();
    if (name.length < 2 || name.length > 16) {
      setErr('昵称需 2~16 个字符');
      return false;
    }
    setErr('');
    setSaving(true);
    try {
      const res = await updateMe({ username: name, avatar_url: avatarUrl });
      onSaved(res.user);
      return true;
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : '保存失败');
      return false;
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-wrap modal-sm">
      <div className="modal-mask" onClick={onClose} />
      <div className="modal-panel">
        <div className="modal-head">
          <p className="modal-title font-zh">更改资料</p>
          <button className="icon-btn" onClick={onClose} aria-label="关闭">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className="flex flex-col items-center">
          <Avatar name={username || user.username} url={avatarPreview} size="xl" ring />
          <p className="mt-1.5 text-[11px] text-t3">预览</p>
        </div>

        <input
          className="field mt-3"
          value={username}
          maxLength={16}
          placeholder="昵称（2~16 字符）"
          onChange={(e) => setUsername(e.target.value)}
        />
        <input
          className="field mt-2"
          value={avatar}
          maxLength={500}
          placeholder="头像图片直链，如 https://…/avatar.png"
          onChange={(e) => {
            setAvatar(e.target.value);
            setErr('');
          }}
        />
        {err ? (
          <p className="mt-1.5 text-[11px] text-cinnabar-500">{err}</p>
        ) : (
          <p className="mt-1.5 text-[11px] leading-relaxed text-t3">
            支持 https 外链直链，建议 200×200 以上方形图；保存后全站生效，恢复默认则清除头像。
          </p>
        )}

        <div className="modal-foot">
          <button
            className="ghost-btn"
            disabled={saving}
            onClick={() => {
              setAvatar('');
              void save(null).then((ok) => ok && showToast('已恢复默认头像'));
            }}
          >
            <i className="fa-solid fa-rotate-left" /> 恢复默认
          </button>
          <button className="gold-btn" disabled={saving} onClick={() => void save(avatarPreview || null)}>
            <i className="fa-solid fa-check" /> {saving ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
}
